import type { Instance, Project, Workspace } from "@/server/types";

// Connection timeout: 60 seconds (3 missed heartbeats at 20s interval)
export const DISCONNECT_THRESHOLD = 60000;

// Number of recent sessions to show before collapsing into "older"
export const RECENT_SESSION_COUNT = 3;

export interface WorkspaceGroup {
  workspace: Workspace;
  instances: Instance[];
  activeInstances: Instance[];
  hasRunning: boolean;
  hasConnected: boolean;
  isDone: boolean;
  latestActivity: number | null;
  latestSeen: number;
}

export interface ProjectGroup {
  project: Project | null;       // null for unassigned instances
  workspace: Workspace | null;   // workspace for this group (if workspace-based)
  groupKey: string;              // project.id, workspace.id, or hostPath/workingDir fallback
  displayName: string;
  instances: Instance[];
  recentInstances: Instance[];
  archivedInstances: Instance[];
  workspaceGroups: WorkspaceGroup[];
  unassignedInstances: Instance[];
  hasRunning: boolean;
  hasConnected: boolean;
  latestSeen: number;
  latestActivity: number | null;
}

export const isConnected = (instance: Instance): boolean =>
  Date.now() - instance.lastSeen < DISCONNECT_THRESHOLD;

export const getStatusColor = (status: Instance["status"], connected: boolean): string => {
  if (!connected) return "bg-zinc-400";
  switch (status) {
    case "running":
      return "bg-green-500";
    case "idle":
      return "bg-yellow-500";
    case "error":
      return "bg-red-500";
  }
};

export const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + "…";
};

export const getProjectDisplayName = (workingDir: string | null, fallbackName: string): string => {
  if (!workingDir) return fallbackName;
  const segments = workingDir.split("/").filter(Boolean);
  return segments[segments.length - 1] || fallbackName;
};

export const isUnread = (instance: Instance): boolean => {
  if (!instance.lastActivity) return false;
  if (!instance.lastViewedAt) return true;
  return instance.lastActivity > instance.lastViewedAt;
};
